import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { DateRangeInterface, DSData, DSSetup, PopupData, Service } from 'webapp-types';
import type {
	GoalCalculationStrategy,
	GoalTargetSuccessCondition,
	GoalTargetValueType,
} from 'webapp/enums/enums';
import { AttachPosition, CompareType, TimeUnit } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal } from 'webapp/utils/common';
import type { TimeUnitForPages } from 'webapp/utils/goal-value-rows';
import {
	DEFAULT_REFERENCE_POINT_VALUE,
	fillValueRowsFromHistoryValues,
	getGoalRangeTimeUnitForPrimaryGranulation,
	getHistoryValuesFromValueRows,
	getPageRangeUnitForTimeUnit,
	getShouldRecalculateAllValues,
	groupValueRowsByTimeUnit,
	PeriodIdFormats,
	recalculateAllValues,
	redistributeFromTimeUnit,
	TargetEndDateFormat,
} from 'webapp/utils/goal-value-rows';
import { getFormattedTime, isTimestampBefore } from 'webapp/utils/number';

import eq from 'webapp/helpers/eq';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import FormInputDurationComponent from 'webapp/components/form/input-duration';
import GoalAdvancedRowComponent from 'webapp/components/goal/advanced/row';
import GoalValueQueryComponent from 'webapp/components/goal/value-query';
import GoalsCalculationStrategyPopupComponent, {
	CALCULATION_STRATEGY_LABELS,
} from 'webapp/components/goals/calculation-strategy-popup';
import GoalsStartingValueComponent from 'webapp/components/goals/starting-value';
import GoalsSuccessConditionComponent from 'webapp/components/goals/success-condition';
import GoalsTargetTypeAndGranulationDropdownComponent from 'webapp/components/goals/target-type-and-granulation/dropdown';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import DateRange from 'webapp/models-custom/date-range';
import { MAX_TARGET_VALUE_LENGTH } from 'webapp/models-custom/goal';
import type GoalValueRow from 'webapp/models-custom/goal/value-row';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import type { GoalRangeTimeUnit } from 'webapp/services/model-manager/goals';
import type { RSjqXHR, RSPromise } from 'webapp/services/request';

export type ValueRowsByTimeUnitAndPageId = Record<TimeUnitForPages, Record<string, GoalValueRow[]>>;

const RECALCULATE_CONFIRM_TEXT =
	'This will overwrite all previously set values—both automatic and manually entered—in other granularities (other tabs). This action cannot be undone.';
const RECALCULATE_CONFIRM_ACTION = 'Recalculate';

interface PreviousValuesData {
	points: DChartPrimaryPoint[];
	granulationLabelsForMatching: DSData.GranulationLabel[];
}

export default class GoalPopupAdvancedSetupComponent extends PopupInnerModalComponent<PopupData.Goal.AdvancedSetup> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/samples')
	declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;

	@tracked targetOtherMetric?: DSSetup.MetricSetupData;
	@tracked valueRows: GoalValueRow[] = [];
	@tracked referencePoint: DChartPrimaryPoint | null = this.popupData.referencePoint;
	@tracked hasTargetValueChanged = false;
	@tracked hasRedistributed = false;
	@tracked referenceValue = String(
		this.popupData.referencePoint?.value || DEFAULT_REFERENCE_POINT_VALUE,
	);
	@tracked updateReferenceInputValue = false;

	targetEndDateFormat = TargetEndDateFormat;
	maxTargetValueLength = MAX_TARGET_VALUE_LENGTH;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.GOAL_TIMETABLE_OPEN, {
			origin: this.popupData.origin,
		});

		this.startValue = this.model.startValue?.toString() || null;

		this.referencePoint = this.popupData.referencePoint;

		// we treat all time as daily periods
		this.selectedTab = getGoalRangeTimeUnitForPrimaryGranulation(this.model.primaryGranulation);

		// Initialize currentPageOffset to show the page containing valuesLastDateTs
		this.currentPageOffset = this.getInitialPageOffsetForValuesLastDate();

		if (this.model.isTargetOtherMetric) {
			this.targetOtherMetric = this.model.otherMetricSetup.metricSetupData || undefined;
		}

		next(() => {
			const updatedValueRows = this.mmGoalsService.updateFormLayout(
				this.valueRows,
				this.valueRowsByTimeUnit,
				this.model.isMainMetricPercentage,
				this.model.isMainMetricInverted,
			);

			this.valueRows = fillValueRowsFromHistoryValues(
				this.model.historyValues,
				updatedValueRows,
			);

			this.mmGoalsService.markRowHiddenAndClearValue(
				this.valueRows,
				this.model.valuesLastDateTs,
			);

			this.fetchReferenceValuesAndSetValueRows();
		});
	}

	get model() {
		return this.popupData.model;
	}

	get metricSetup() {
		return this.popupData.metricSetup;
	}

	get showLoader() {
		return !this.currentPageValueRows.length;
	}

	@action
	triggerContentChange() {
		this.scheduleReposition();
	}

	// Reference value (last full period of primary timeUnit)

	get isManualReferenceValueInputShown() {
		return this.model.useEndDateForCalculations && !this.isReferenceValueLoading;
	}

	get isValidTargetValueEntered() {
		return typeof this.model.targetValue === 'number';
	}

	get isReferenceValueLoading() {
		return !!this.mmGoalsService.isReferenceValueLoading;
	}

	get isSaveDisabled() {
		return this.isReferenceValueLoading;
	}

	// Value Rows create / update

	@cached
	get valueRowsByTimeUnit() {
		return groupValueRowsByTimeUnit(this.valueRows);
	}

	@cached
	get valueRowsByTimeUnitAndPageId(): ValueRowsByTimeUnitAndPageId {
		const rowsByTimeUnitAndPageId: ValueRowsByTimeUnitAndPageId = {
			[TimeUnit.DAY]: {},
			[TimeUnit.WEEK]: {},
			[TimeUnit.MONTH]: {},
			[TimeUnit.QUARTER]: {},
			[TimeUnit.YEAR]: {},
		};
		Object.keys(this.valueRowsByTimeUnit).forEach((timeUnitString) => {
			const timeUnit: TimeUnitForPages = +timeUnitString,
				valueRowsByTimeUnit = this.valueRowsByTimeUnit[timeUnit],
				valueRowsByPageId: Record<string, GoalValueRow[]> = {};
			valueRowsByTimeUnit.forEach((valueRowByTimeUnit) => {
				if (valueRowsByPageId[valueRowByTimeUnit.pageId]) {
					valueRowsByPageId[valueRowByTimeUnit.pageId]!.push(valueRowByTimeUnit);
				} else {
					valueRowsByPageId[valueRowByTimeUnit.pageId] = [valueRowByTimeUnit];
				}
			});
			rowsByTimeUnitAndPageId[timeUnit] = valueRowsByPageId;
		});

		return rowsByTimeUnitAndPageId;
	}

	@cached
	get currentPageValueRows() {
		// get value rows for current page
		const currentPageId = this.currentPageId;

		if (currentPageId) {
			return this.valueRowsByTimeUnitAndPageId[this.selectedTab]?.[currentPageId] || [];
		} else {
			return [];
		}
	}

	@cached
	get primaryGranulationValueRows() {
		// value rows for primary granulation of the goal
		// we treat all time as daily periods
		const primaryGranulation = getGoalRangeTimeUnitForPrimaryGranulation(
			this.model.primaryGranulation,
		);
		return this.valueRowsByTimeUnit[primaryGranulation];
	}

	@cached
	get currentSelectedTabValueRows() {
		return this.valueRowsByTimeUnit[this.selectedTab];
	}

	get dateRangeCM() {
		const dateRangeFormula: DateRangeInterface.FormulaInterface = {
			range: {
				includeFuture: true,
				timeUnit: this.model.primaryGranulation,
				multiplier: 1,
				offset: -1,
			},
			granulation: {
				isWholeRange: true,
			},
		};
		return DateRange.createFromFormula(dateRangeFormula);
	}

	private recalculateAllValues() {
		if (!this.isValidTargetValueEntered) {
			return;
		}

		recalculateAllValues(
			this.valueRowsByTimeUnit,
			this.model.primaryGranulation,
			this.model.isTargetValuePercentFormat,
			this.model.targetValue!,
			this.model.startDateTsFromTimeframe,
			this.model.valuesLastDateTs,
			this.model.useEndDateForCalculations,
			this.model.mainMetricType,
			this.referencePoint?.value,
		);

		this.valueRows.forEach((r) => {
			r.markClean();
		});

		this.mmGoalsService.markRowHiddenAndClearValue(this.valueRows, this.model.valuesLastDateTs);
	}

	// Pages navigation

	@tracked currentPageOffset: number; // 0 - current, -1 - previous, +1 - next, etc

	private getInitialPageOffsetForValuesLastDate(): number {
		const valuesLastDateTs = this.model.valuesLastDateTs;
		const pages = this.mmGoalsService.formLayoutCache[this.selectedTab];

		if (this.selectedTab === TimeUnit.DAY || !valuesLastDateTs || !pages?.length) {
			return 0;
		}

		const matchingPage = pages.find((page) =>
			page.items.some(
				(item) =>
					valuesLastDateTs >= item.periodStartTS && valuesLastDateTs <= item.periodEndTS,
			),
		);

		return matchingPage?.pageOffset ?? 0;
	}

	@cached
	get currentPage() {
		const currentPageOffset = this.currentPageOffset;
		return this.mmGoalsService.formLayoutCache[this.selectedTab]?.find(
			(page) => page.pageOffset === currentPageOffset,
		);
	}

	@cached
	get currentPageId() {
		return this.currentPage?.pageId;
	}

	get showPageNavigation() {
		return this.currentPageId && this.currentPageId !== 'all';
	}

	get currentPageTitle() {
		return this.getPageTitleForOffset(this.currentPageOffset);
	}

	get currentPageTitleTooltip() {
		const items = this.currentPage?.items;
		if (!items?.length) {
			return '';
		}
		const format = 'MMM YYYY';
		const tz = this.currentUserService.model?.timezone;
		const startLabel = getFormattedTime(items[0]!.periodStartTS, tz, format);
		const endLabel = getFormattedTime(items[items.length - 1]!.periodEndTS - 1, tz, format);
		return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
	}

	get previousPageTitle() {
		return this.getPageTitleForOffset(this.currentPageOffset - 1);
	}

	get nextPageTitle() {
		return this.getPageTitleForOffset(this.currentPageOffset + 1);
	}

	get canGoPrevious() {
		if (!this.model.valuesLastDateTs) {
			return true;
		}
		return !!this.previousPageTitle;
	}

	get canGoNext() {
		if (!this.model.valuesLastDateTs) {
			return true;
		}
		return !!this.nextPageTitle;
	}

	get paginationTimeUnit() {
		return getPageRangeUnitForTimeUnit(this.selectedTab) as TimeUnitForPages;
	}

	get currentPageIdFormat() {
		return PeriodIdFormats[this.paginationTimeUnit];
	}

	get currentPageDateMoment() {
		return moment(this.currentPageId, this.currentPageIdFormat);
	}

	get pagesEndDateMoment() {
		const ts = this.model.valuesLastDateTs;
		return ts ? moment.unix(ts) : null;
	}

	private getPageTitleForOffset(pageOffset: number) {
		const currentTimeUnit = this.selectedTab;

		if (currentTimeUnit === TimeUnit.YEAR) {
			return '';
		}

		const pages = this.mmGoalsService.formLayoutCache[currentTimeUnit] || [];
		const matchedPage = pages.find((page) => page.pageOffset === pageOffset);

		const pagesEndDateTs = this.model.valuesLastDateTs;
		if (pagesEndDateTs && matchedPage) {
			const pageStartTs = matchedPage.items[0]?.periodStartTS;
			if (pageStartTs !== undefined && pageStartTs > pagesEndDateTs) {
				return '';
			}
		}

		return matchedPage?.pageName;
	}

	@action
	goNext() {
		this.currentPageOffset += 1;

		this.checkToRequestCurrentPageFormLayout();
	}

	@action
	goPrevious() {
		this.currentPageOffset -= 1;

		this.checkToRequestCurrentPageFormLayout();
	}

	private checkToRequestCurrentPageFormLayout() {
		// if there is no currentPageValueRows - request layout for the page
		if (!this.currentPageValueRows.length) {
			const dateRange = DateRange.createFromFormula({
				range: {
					includeFuture: true,
					timeUnit: getPageRangeUnitForTimeUnit(this.selectedTab),
					multiplier: 1,
					offset: this.currentPageOffset,
				},
			});
			void this.mmGoalsService.requestFormLayout(dateRange).then(() => {
				this.valueRows = this.mmGoalsService.updateFormLayout(
					this.valueRows,
					this.valueRowsByTimeUnit,
					this.model.isMainMetricPercentage,
					this.model.isMainMetricInverted,
				);
				this.checkAndRecalculateAllValues();
				this.fetchReferenceValuesAndSetValueRows();
			});
		} else {
			this.fetchReferenceValuesAndSetValueRows();
		}
	}

	// Tabs navigation

	@tracked selectedTab: TimeUnitForPages = TimeUnit.MONTH;

	tabs = [
		{ id: TimeUnit.DAY, text: 'Daily' },
		{ id: TimeUnit.WEEK, text: 'Weekly' },
		{ id: TimeUnit.MONTH, text: 'Monthly' },
		{ id: TimeUnit.QUARTER, text: 'Quarterly' },
		{ id: TimeUnit.YEAR, text: 'Yearly' },
	];

	// click on tab
	@action
	openTab(tab: number) {
		this.selectedTab = tab;
		this.currentPageOffset = this.getInitialPageOffsetForValuesLastDate();

		this.fetchReferenceValuesAndSetValueRows();
		this.triggerContentChange();
	}

	// when user changes primary granulation
	@action
	changeTargetRange(
		targetValueType: GoalTargetValueType,
		primaryGranulation: GoalRangeTimeUnit,
		otherMetric?: DSSetup.MetricSetupData,
	) {
		const currentPrimaryGranulation = this.model.primaryGranulation;
		const currentTargetValueType = this.model.targetValueType;

		this.model.targetValueType = targetValueType;
		this.model.primaryGranulation = primaryGranulation;
		this.targetOtherMetric = otherMetric;

		this.checkToSetDefaultTargetValueOnPercentualChange();

		if (
			(currentPrimaryGranulation !== primaryGranulation &&
				currentTargetValueType === targetValueType) ||
			currentTargetValueType !== targetValueType
		) {
			// current primaryGranulation and targetValueType is not the same as the new one
			this.fetchAndSetReferenceValueAndRecalculate();
		}
	}

	@tracked startValue!: string | null;

	@action
	changeStartValue(value: number | string | null) {
		// setGoalStartValue
		const stringValueForInput = this.model.setStartValueFromUserInput(value);
		this.startValue = stringValueForInput;

		// setReferencePoint
		if (this.mmGoalsService.isValueValid(value)) {
			const pointData = {
				value: Number(value),
				valueStr: String(value),
			};

			this.referencePoint = new DChartPrimaryPoint(pointData);
		} else {
			this.referencePoint = null;
		}

		this.checkAndRecalculateAllValues();
	}

	@action
	changeSuccessCondition(value: GoalTargetSuccessCondition) {
		if (this.model.targetSuccessCondition === value) {
			return;
		}

		this.model.targetSuccessCondition = value;

		this.model.checkPrimaryGranulation();
		void this.fetchReferenceValue().then((referencePoint) => {
			this.referencePoint = referencePoint;
		});

		this.checkAndRecalculateAllValues();
	}

	// when goal target value is changed
	@action
	changeTargetValue(value: number | null) {
		this.hasTargetValueChanged = true;
		this.model.targetValue = value;

		this.checkAndRecalculateAllValues();
	}

	// when goal end date is changed
	@action
	changeEndDate(date: string) {
		if (this.model.endDate === date) {
			return;
		}

		const newEndDateTs = this.mmGoalsService.getTargetEndDateTimestamp(date);
		const previousValue = this.model.endDate;
		if (isTimestampBefore(newEndDateTs, this.model.startDateTsFromTimeframe)) {
			this.infolayerService.error(
				'The selected end date is before the timeframe start date. Please select a later date.',
			);
			this.model.endDate = date;
			next(() => {
				this.model.endDate = previousValue;
			});
			return;
		}

		this.scheduleReposition();

		this.model.endDate = date || null;

		this.model.checkSuccessCondition();
		this.model.checkPrimaryGranulation();

		const endDateYear = this.pagesEndDateMoment?.year(),
			currentPageYear = this.currentPageDateMoment.year();

		if (currentPageYear && endDateYear && currentPageYear >= endDateYear) {
			this.currentPageOffset = 0;
		}

		void this.mmGoalsService.checkToRebuildFullForm(this.valueRows, this.model).then(() => {
			this.valueRows = this.mmGoalsService.updateFormLayout(
				this.valueRows,
				this.valueRowsByTimeUnit,
				this.model.isMainMetricPercentage,
				this.model.isMainMetricInverted,
			);

			this.mmGoalsService.markRowHiddenAndClearValue(
				this.valueRows,
				this.model.valuesLastDateTs,
			);

			this.checkAndRecalculateAllValues();
		});
	}

	@action
	changeReferenceValue(referenceValue: number | null) {
		if (this.mmGoalsService.isValueValid(referenceValue)) {
			this.referenceValue = String(referenceValue);
		} else {
			this.referenceValue = DEFAULT_REFERENCE_POINT_VALUE.toString();
			this.updateReferenceInputValue = true;

			next(() => {
				this.updateReferenceInputValue = false;
			});
		}

		this.referencePoint = new DChartPrimaryPoint({
			value: Number(this.referenceValue),
			valueStr: this.referenceValue,
		});

		this.checkAndRecalculateAllValues();
	}

	@action
	checkToSetDefaultTargetValueOnPercentualChange() {
		if (this.hasTargetValueChanged || !this.model.isNew) {
			return;
		}
		// set target value on model
		const isValuesPercentual =
			this.model.isHistoryValuesPercentFormat || this.model.isTargetValuePercentFormat;

		const value = this.mmGoalsService.getDefaultTargetValue(isValuesPercentual);
		this.model.targetValue = value;

		this.recalculateAllValues();
	}

	@action
	fillSubsequentEmptyRows(currentRow: GoalValueRow, value: number | null) {
		this.hasRedistributed = false;

		if (value === null || value === undefined) {
			return;
		}

		const currentTabRows = this.currentSelectedTabValueRows
			.slice()
			.sort((a, b) => a.periodStartTs - b.periodStartTs);
		const currentRowIndex = currentTabRows.findIndex((row) => row.id === currentRow.id);

		if (currentRowIndex === -1) {
			return;
		}

		for (let i = currentRowIndex + 1; i < currentTabRows.length; i++) {
			const row = currentTabRows[i]!;
			if (row.hasValue) {
				break;
			}
			row.updateValue(value);
		}
	}

	get needsRecalculateConfirmOnSave() {
		return (
			this.model.historyValuesOverride &&
			this.currentSelectedTabValueRows.some((r) => r.isDirty) &&
			!this.hasRedistributed
		);
	}

	// save history values and close popup
	@action
	onSave() {
		if (this.needsRecalculateConfirmOnSave) {
			this.popupService.openConfirmPopup({
				onConfirm: () => {
					this.redistribute();
					this.saveAndClose();
				},
				onReject: () => {
					this.saveAndClose();
				},
				title: "You've changed the targets in this tab. It's recommended to recalculate other tabs to keep everything consistent.",
				text: RECALCULATE_CONFIRM_TEXT,
				confirmAction: RECALCULATE_CONFIRM_ACTION,
				cancelAction: 'Save only',
			});
			return;
		}

		this.saveAndClose();
	}

	private saveAndClose() {
		const newHistoryValues = getHistoryValuesFromValueRows(this.valueRowsByTimeUnit);

		if (!equal(this.model.historyValues, newHistoryValues)) {
			this.analyticsService.event(TrackingEvent.GOAL_TIMETABLE_CHANGED);
		}

		this.model.historyValues = newHistoryValues;

		this.model.otherMetricSetup.setFromMetricSetupData(this.targetOtherMetric || null);

		this.popupData.onSave(this.model, this.referencePoint);

		this.close();
	}

	// override history values
	get overrideValuesText() {
		return this.model.historyValuesOverride
			? 'Custom changes override the main target'
			: 'Values are auto-calculated from main target';
	}

	get overrideValuesActionText() {
		return this.model.historyValuesOverride ? 'Revert to auto-calculation' : 'Adjust manually';
	}

	@action
	toggleOverrideValues() {
		this.model.historyValuesOverride = !this.model.historyValuesOverride;

		if (!this.model.historyValuesOverride) {
			// recalculate all values
			this.recalculateAllValues();
		}
	}

	get isAdvancedRowsDisabled() {
		return !this.model.historyValuesOverride || this.isReferenceValueLoading;
	}

	get isAdvancedFormDisabled() {
		return this.model.historyValuesOverride || this.isReferenceValueLoading;
	}

	// --- calculation strategy ---

	get showCalculationStrategy() {
		return this.model.isAutomaticGoal && !this.model.isTargetOtherMetric;
	}

	get isCalculationStrategyDisabled() {
		return this.model.historyValuesOverride || this.isReferenceValueLoading;
	}

	get calculationStrategyLabel() {
		return CALCULATION_STRATEGY_LABELS[this.model.calculationStrategy];
	}

	@action
	openCalculationStrategyPopup(event: MouseEvent) {
		if (this.isCalculationStrategyDisabled) {
			return;
		}

		const attachTo = (event.currentTarget as HTMLElement).querySelector<HTMLElement>(
			'.calculation-strategy-trigger__override',
		);

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(
			GoalsCalculationStrategyPopupComponent,
			attachTo,
			{
				popupClass: 'optionspopup popup-type-goal-calculation-strategy',
				attachPosition: AttachPosition.END,
				alignEdgeWithAttachTo: true,
			},
		);

		popup.open({
			selected: this.model.calculationStrategyOverride,
			autoStrategy: this.model.autoCalculationStrategy,
			isSumAllowed: this.model.isSumCalculationStrategyAllowed,
			onApply: this.changeCalculationStrategy,
		});
	}

	@action
	changeCalculationStrategy(strategy: GoalCalculationStrategy | null) {
		if (strategy === this.model.calculationStrategyOverride) {
			return;
		}

		this.model.calculationStrategyOverride = strategy;
		this.analyticsService.event(TrackingEvent.GOAL_CALCULATION_STRATEGY_CHANGED, {
			strategy: strategy ?? 'default',
		});

		this.recalculateAllValues();
	}

	// --- redistribute ---

	get showRedistribute() {
		// show redistribute if there are any dirty rows on any page
		return (
			this.currentPageValueRows.length &&
			this.currentSelectedTabValueRows.some((r) => r.isDirty) &&
			this.model.historyValuesOverride
		);
	}

	// in other words recalculate all other values except current time unit
	@action
	recalculateOtherTabs() {
		this.popupService.openConfirmPopup({
			onConfirm: () => this.redistribute(),
			title: 'Recalculate goal values in other tabs?',
			text: RECALCULATE_CONFIRM_TEXT,
			confirmAction: RECALCULATE_CONFIRM_ACTION,
			cancelAction: 'Cancel',
		});
	}

	private redistribute() {
		const redistributeFn = () => {
			redistributeFromTimeUnit(
				this.valueRowsByTimeUnit,
				this.model.primaryGranulation,
				this.selectedTab,
				this.model.mainMetricType,
				this.model.startDateTsFromTimeframe,
				this.model.valuesLastDateTs,
			);
			this.hasRedistributed = true;
		};

		if (this.selectedTab === TimeUnit.YEAR) {
			// exception for yearly - we might have updated a row that we havent loaded layout yet for on other granulations
			const yearlyTimeUnitValueRows = this.valueRowsByTimeUnit[TimeUnit.YEAR];
			const currentYearIndex = yearlyTimeUnitValueRows.indexOf(
				yearlyTimeUnitValueRows.find((r) => r.isActive)!,
			);
			const yearOffsetsWithValues = yearlyTimeUnitValueRows
				.map((row, idx) => (row.hasValue ? idx - currentYearIndex : null))
				.filter((v) => v !== null);
			const minYearOffset = Math.min(...yearOffsetsWithValues),
				maxYearOffset = Math.max(...yearOffsetsWithValues);

			if (minYearOffset && maxYearOffset) {
				let minQuarterOffset = 0,
					maxQuarterOffset = 0;
				this.mmGoalsService.formLayoutCache[TimeUnit.QUARTER]!.forEach((page) => {
					if (page.pageOffset < minQuarterOffset) {
						minQuarterOffset = page.pageOffset;
					}
					if (page.pageOffset > maxQuarterOffset) {
						maxQuarterOffset = page.pageOffset;
					}
				});
				const promises: Promise<unknown>[] = [];
				if (minQuarterOffset > minYearOffset) {
					// request past
					const multiplier = minQuarterOffset - minYearOffset;
					const dateRangeToRequest = DateRange.createFromFormula({
						range: {
							includeFuture: true,
							timeUnit: TimeUnit.YEAR,
							multiplier,
							offset: -1 - minQuarterOffset,
						},
					});
					promises.push(this.mmGoalsService.requestFormLayout(dateRangeToRequest));
				}
				if (maxQuarterOffset < maxYearOffset) {
					// request future
					const multiplier = maxYearOffset - maxQuarterOffset;
					const dateRangeToRequest = DateRange.createFromFormula({
						range: {
							includeFuture: true,
							timeUnit: TimeUnit.YEAR,
							multiplier,
							offset: multiplier + maxQuarterOffset,
						},
					});
					promises.push(this.mmGoalsService.requestFormLayout(dateRangeToRequest));
				}
				void Promise.all(promises).then(() => {
					redistributeFn();
				});
				return;
			}
		}

		redistributeFn();
	}

	private fetchReferenceValue() {
		return this.mmGoalsService.fetchReferenceValue(
			this.metricSetup,
			this.model.primaryGranulation,
			this.model.startDateTsFromTimeframe,
			this.model.endDateTsFromTimeframe,
		);
	}

	private checkAndRecalculateAllValues() {
		const shouldRecalculateAllValues = getShouldRecalculateAllValues(
			this.isValidTargetValueEntered,
			this.model.historyValuesOverride,
		);

		if (!this.model.isTargetOtherMetric && shouldRecalculateAllValues) {
			this.recalculateAllValues();
		}
	}

	// --- Fetch previous values ---
	get isPreviousValuesLoading() {
		return !!this._fetchPreviousValuesPromise;
	}

	getComparePointForTimestamp(previousPoints: DChartPrimaryPoint[], timestamp: number) {
		return previousPoints.find((point) => point.timestamp === timestamp)?.comparePoints[0];
	}

	fetchReferenceValuesAndSetValueRows() {
		if (!this.model.isAutomaticGoal) {
			return;
		}

		const pageRows = this.currentPageValueRows;

		pageRows.forEach((valueRow) => {
			valueRow.previousPoint = null;
		});

		void this.fetchPreviousValues().then((previousValuesData) => {
			if (!previousValuesData) {
				return;
			}

			const { points, granulationLabelsForMatching } = previousValuesData;

			// bcos timestamps for goal form (value rows) might not match data timestamps (bcos of source timezones)
			// we find the "closest" row for each data timestamp
			// max error is half of diff between two row timestamps
			const maxError = (pageRows[1]!.periodStartTs - pageRows[0]!.periodStartTs) / 2;

			granulationLabelsForMatching.forEach(
				(granulationLabel: DSData.GranulationLabel, idx) => {
					// primaryPoint is the previous year point
					// that we must match to goal row that have this year timestamp
					// use granulation label (from compare) for that
					const primaryPoint = points[idx],
						startTs = granulationLabel.ts;

					if (!primaryPoint) {
						return;
					}

					const matchFromTs = startTs - maxError,
						matchToTs = startTs + maxError;

					const row = pageRows.find(
						(r) => r.periodStartTs >= matchFromTs && r.periodStartTs < matchToTs,
					);

					if (row) {
						row.previousPoint = primaryPoint;
					}
				},
			);
		});
	}

	@tracked private _fetchPreviousValuesPromise: RSPromise<unknown> | null = null;
	fetchPreviousValues(): Promise<PreviousValuesData | null> {
		const isPaginationTimeunitTotal =
			this.paginationTimeUnit === (TimeUnit.TOTAL as TimeUnitForPages);

		const timeUnit = isPaginationTimeunitTotal ? TimeUnit.YEAR : this.paginationTimeUnit;
		const multiplier = isPaginationTimeunitTotal ? 10 : 1;
		const offset = isPaginationTimeunitTotal ? 4 : this.currentPageOffset;

		// we want to request the current page data with 1 year offset
		// primary range does not support diff timeUnit for offset (e.g. this month daily offset by 1 year)
		// the easiest way would be to request same period last year as compare but BE returns only compares that have current value
		// discussion here: https://databox.slack.com/archives/C06Q7F6M9PZ/p1742381265475399?thread_ts=1742376808.006469&cid=C06Q7F6M9PZ)
		// so instead we adjust offset for those pages that do not show whole year (e.g. daily timeUnit showing 1 month)
		// and request current as compare so we can match timestamps (see comment below)
		const yearlyOffset = this.paginationTimeUnit === TimeUnit.MONTH ? 12 : 1;

		const dataSelect = this.mmDataSelectsService.createNewModel({
			dateRange: {
				range: {
					timeUnit,
					multiplier,
					offset: offset - yearlyOffset,
					includeFuture: true,
				},
				granulation: {
					timeUnit: this.selectedTab,
					multiplier: 1,
					isWholeRange: false,
				},
			},
			metricSetupKey: this.metricSetup.key,
			metricSetupSpaceAccessId: this.metricSetup.spaceAccessId,
			metricSetupDimensions: this.metricSetup.dimensions,
			metricSetupFilters: this.metricSetup.filtersData,
			// adding compare so we can match the previous data timestamps with the current goal rows
			// otherwise we'd need to hardcode how many seconds in a year (which is unreliable)
			// data will not be used, just granulation labels for matching
			compares: [
				{
					type: CompareType.RANGE,
					dateRange: {
						offset: {
							timeUnit: TimeUnit.YEAR,
							offset: 1,
						},
					},
				},
			],
		});

		const previousPromise = this._fetchPreviousValuesPromise;
		if (previousPromise) {
			(previousPromise.jqPromise as RSjqXHR | undefined)?.abort();
		}

		const promise = this.mmDataSelectsService
			.fetchData(dataSelect)
			.then((reply: DSData.RootTop) => {
				dataSelect.setFakeData(reply);

				const output = dataSelect.outputs[0];
				if (output) {
					return {
						points: this.mmSamplesService.createDChartPointsFromOutput(output),
						granulationLabelsForMatching:
							output.compareOutputs[0]?.dataRoot?.granulationLabels || [],
					};
				}

				return null;
			})
			.catch(() => {
				this.infolayerService.error('Failed to fetch previous values. Try again later.');
				return null;
			})
			.finally(() => {
				// cleanup promise and model
				if (promise === this._fetchPreviousValuesPromise) {
					this._fetchPreviousValuesPromise = null;
				}
				void dataSelect.delete(true);
			});

		this._fetchPreviousValuesPromise = promise;

		return promise;
	}

	@action
	destroyView(): void {
		const referenceValuePromise = this.mmGoalsService._fetchReferenceValuePromise;
		if (referenceValuePromise) {
			(referenceValuePromise.jqPromise as RSjqXHR | undefined)?.abort();
		}
	}

	private fetchAndSetReferenceValueAndRecalculate() {
		this.fetchReferenceValue()
			.then((referencePoint) => {
				this.referencePoint = referencePoint;

				this.referenceValue = String(
					referencePoint?.value || DEFAULT_REFERENCE_POINT_VALUE,
				);
				this.updateReferenceInputValue = true;

				next(() => {
					this.updateReferenceInputValue = false;
				});
			})
			.catch(() => {
				// Fallback to default value
				this.referencePoint = null;
			})
			.finally(() => {
				this.checkAndRecalculateAllValues();
			});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Settings" @size="lg" />
			Customize goal target values
		</div>

		<div class="content" {{willDestroy this.destroyView}}>
			<div class="form-row form-row--top">
				{{#if this.model.isManualGoal}}
					<div class="form-column">
						<span class="label">Starting value</span>
						<GoalsStartingValueComponent
							@model={{this.model}}
							@value={{this.startValue}}
							@onChange={{this.changeStartValue}}
							@isDisabled={{this.isAdvancedFormDisabled}}
						/>
					</div>
				{{else}}
					<div class="form-column reference-value">
						<span class="label">Reference value</span>
						{{#if this.isManualReferenceValueInputShown}}
							{{#if this.model.useDurationInput}}
								<FormInputDurationComponent
									@value={{this.referenceValue}}
									@onChange={{this.changeReferenceValue}}
									@forceUpdateFromValue={{this.updateReferenceInputValue}}
									@isDisabled={{this.isAdvancedFormDisabled}}
								/>
							{{else}}
								<FormInputDecimalComponent
									@value={{this.referenceValue}}
									@onChange={{this.changeReferenceValue}}
									@placeholder="Reference value"
									@forceUpdateFromValue={{this.updateReferenceInputValue}}
									@maxLength={{this.maxTargetValueLength}}
									@isDisabled={{this.isAdvancedFormDisabled}}
								/>
							{{/if}}
						{{else}}
							<div class="value-query">
								{{#if this.isReferenceValueLoading}}
									<div class="loader">
										<BarLoaderComponent @size="tiny" />
										Gathering data...
									</div>
								{{else}}
									<GoalValueQueryComponent
										@text={{this.dateRangeCM.rangeTitleUX}}
										@valueString={{this.referencePoint.valueStr}}
									/>
								{{/if}}
							</div>
						{{/if}}
					</div>
				{{/if}}
				<div class="form-column">
					<span class="label">Success condition</span>
					<GoalsSuccessConditionComponent
						@model={{this.model}}
						@submitInProgress={{this.isAdvancedFormDisabled}}
						@onChange={{this.changeSuccessCondition}}
					/>
				</div>
				<div class="form-column target-value">
					<span class="label">Target value</span>
					<GoalsTargetTypeAndGranulationDropdownComponent
						@model={{this.model}}
						@targetOtherMetric={{this.targetOtherMetric}}
						@changeTargetValue={{this.changeTargetValue}}
						@changeTargetRange={{this.changeTargetRange}}
						@hideOtherMetric={{true}}
						@isDisabled={{this.isAdvancedFormDisabled}}
					/>
				</div>
				<div class="form-column goal-end">
					<span class="label">Custom due date</span>
					<FormDatetimePickerComponent
						@dateFormat={{this.targetEndDateFormat}}
						@value={{this.model.endDate}}
						@disableTimePicker={{true}}
						@isDisabled={{this.isReferenceValueLoading}}
						@onChange={{this.changeEndDate}}
						@placeholder="Optional"
						@showClearButton={{true}}
						@disablePast={{true}}
						@hideFloatLabel={{true}}
					/>
				</div>
			</div>
		</div>
		<div class="content content--secondary">
			<div class="form-row tabs">
				<ul class="modalTabs modalTabs-2">
					{{#each this.tabs as |tab|}}
						<li
							class="navigation-button
								{{if (eq this.selectedTab tab.id) 'navigation-button--selected'}}"
						>
							<a role="button" {{on "click" (fn this.openTab tab.id)}}>
								{{tab.text}}
							</a>
						</li>
					{{/each}}
				</ul>

				<div class="tabs__actions">
					{{#if this.showCalculationStrategy}}
						<a
							role="button"
							class="calculation-strategy-trigger
								{{if this.isCalculationStrategyDisabled 'disabled'}}"
							{{on "click" this.openCalculationStrategyPopup}}
						>
							<CommonUiIconComponent @name="Formula" @size="sm" />
							<span class="calculation-strategy-trigger__label">Calculation:</span>
							<span class="calculation-strategy-trigger__value">
								{{this.calculationStrategyLabel}}
							</span>
							{{#if this.model.isCalculationStrategyOverridden}}
								<span class="badge2 badge2--blue">Overridden</span>
							{{else}}
								<span class="badge2 badge2--outline">Default</span>
							{{/if}}
							<span class="link link--blue calculation-strategy-trigger__override">
								Override
							</span>
						</a>
					{{/if}}

					{{#if this.showRedistribute}}
						<SubmitButtonComponent
							@text="Recalculate other tabs"
							@theme="blue-empty"
							@icon="Refresh"
							@onTrigger={{this.recalculateOtherTabs}}
							@isDisabled={{this.isReferenceValueLoading}}
						/>
					{{/if}}
				</div>
			</div>
			<hr class="wider" />
			<div class="page">
				{{#if this.showLoader}}
					<div class="row-wrapper loading" {{didInsert this.triggerContentChange}}>
						<BarLoaderComponent />
					</div>
				{{else}}
					<div
						class="row-wrapper layout-{{this.selectedTab}}"
						{{didInsert this.triggerContentChange}}
					>
						{{#each this.currentPageValueRows as |row|}}
							{{#unless row.isHidden}}
								<GoalAdvancedRowComponent
									@isTargetPercentual={{this.model.isTargetValuePercentFormat}}
									@model={{row}}
									@isPreviousValuesLoading={{this.isPreviousValuesLoading}}
									@isDuration={{this.model.isDurationMetric}}
									@isValuesPercentual={{this.model.isHistoryValuesPercentFormat}}
									@onValueChange={{this.fillSubsequentEmptyRows}}
									@isDisabled={{this.isAdvancedRowsDisabled}}
								/>
							{{/unless}}
						{{/each}}
					</div>
				{{/if}}
			</div>
			{{#if this.showPageNavigation}}
				<div class="navigation">
					{{#if this.canGoPrevious}}
						<a role="button" class="prev" {{on "click" this.goPrevious}}>
							<CommonUiIconComponent @name="ArrowLeft" @size="sm" />
							{{this.previousPageTitle}}
						</a>
					{{/if}}

					<span>
						{{this.currentPageTitle}}
						{{#if this.currentPageTitleTooltip}}
							<ToolTipComponent
								@text={{this.currentPageTitleTooltip}}
								@maxWidth={{300}}
							/>
						{{/if}}
					</span>

					{{#if this.canGoNext}}
						<a role="button" class="next" {{on "click" this.goNext}}>
							{{this.nextPageTitle}}
							<CommonUiIconComponent @name="ArrowRight" @size="sm" />
						</a>
					{{/if}}
				</div>
			{{/if}}
			<hr class="wider" />
			<div class="footer">
				<div class="values-override">
					<span>{{this.overrideValuesText}}</span>
					<a
						role="button"
						class="link link--blue {{if this.isReferenceValueLoading 'disabled'}}"
						{{on "click" this.toggleOverrideValues}}
					>
						{{this.overrideValuesActionText}}
					</a>
				</div>
				<div class="controls2">
					<div class="controls2__right">
						<SubmitButtonComponent
							@text="Cancel"
							@theme="grey-empty"
							@noDelay={{true}}
							@onTrigger={{this.close}}
						/>
						<SubmitButtonComponent
							@text="Save"
							@extraClass="save"
							@onTrigger={{this.onSave}}
							@isDisabled={{this.isSaveDisabled}}
						/>
					</div>
				</div>
			</div>
		</div>
	</template>
}
